import { useCallback, useEffect, useState } from 'react'

interface UseIntersectionObserverOptions {
  threshold?: number
  rootMargin?: string
  enabled?: boolean
}

/**
 * Returns a callback ref + intersection flag. Using a callback ref (not useRef)
 * ensures the observer re-attaches whenever the target element mounts or
 * remounts — critical when the sentinel is conditionally rendered (e.g. only
 * while `hasNextPage` is true).
 */
export function useIntersectionObserver({
  threshold = 0.1,
  rootMargin = '100px',
  enabled = true,
}: UseIntersectionObserverOptions = {}) {
  const [isIntersecting, setIsIntersecting] = useState(false)
  const [node, setNode] = useState<HTMLElement | null>(null)

  const targetRef = useCallback((el: HTMLElement | null) => {
    setNode(el)
  }, [])

  useEffect(() => {
    if (!enabled || !node) {
      setIsIntersecting(false)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting)
      },
      { threshold, rootMargin }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [node, threshold, rootMargin, enabled])

  return { targetRef, isIntersecting }
}
